import { ExtendedColumnType, useColumnExpansion } from "src/pages/binOverview/BinCommander";
import dayjs, { Dayjs } from "dayjs";
import { Table } from "antd";
import React, { useMemo } from "react";
import PremierPlusTapestripDTO from "src/models/PremierPlusTapestripDTO";
import { useGetPremierPlusBinTapestripData } from "src/queries/useGetPremierPlusBinTapestripData";
import "./table.css";
import { formatNumber } from "src/utils/formatting";
import PremierPlusBinDTO from "src/models/PremierPlusBinDTO";
import { convertFahrenheitToCelsius } from "src/utils/generalUtils";
import { RedditCircleFilled } from "@ant-design/icons";
import { record } from "zod";

interface PremierPlusTapestripTableProps {
  binId: number;
  dateRange: [Dayjs | null, Dayjs | null];
  setDateRange: (date: [Dayjs | null, Dayjs | null]) => void;
  binDto: PremierPlusBinDTO | null | undefined;
}

export const PremierPlusTapeStripTable: React.FC<PremierPlusTapestripTableProps> = ({
  binId,
  dateRange,
  setDateRange,
  binDto,
}) => {
  const useCelsius = useMemo(
    () => binDto?.platformSettings?.useMetricSystem === true,
    [binDto]
  );
  const unit = useMemo(() => (useCelsius ? "°C" : "°F"), [useCelsius]);

  const startIso = dateRange[0]?.toISOString() ?? null;
  const endIso = dateRange[1]?.toISOString() ?? null;

  const { getPremierPlusTapestripDataQuery } = useGetPremierPlusBinTapestripData(
    binId,
    startIso,
    endIso,
    { enabled: true, refetchOnWindowFocus: false }
  );

  const toTemp = useMemo(
    () => (v?: number | null) =>
      v == null ? v : useCelsius ? convertFahrenheitToCelsius(v) : v,
    [useCelsius]
  );

  const columns: Array<ExtendedColumnType<PremierPlusTapestripDTO>> = useMemo(
    () => [
      {
        title: "Datein",
        dataIndex: "captureTimeUtc",
        key: "captureTimeUtc",
        sorter: (a, b) => dayjs(a.captureTimeUtc).diff(b.captureTimeUtc),
        render(value) {
          return <span>{dayjs(value).format("hh:mm A MM/DD/YYYY")}</span>;
        },
        sortType: "date",
        defaultSortOrder: "descend",
      },
      {
        title: "Grain Moisture (%)",
        dataIndex: "individualMc",
        key: "individualMc",
        render: (value) => formatNumber(value, { decimalPlaces: 1 }),
      },
      {
        title: `Grain Temp (${unit})`,
        dataIndex: "grainTemperatureF",
        key: `grainTemperature-${unit}`, // key changes when unit changes
        render: (value) => formatNumber(toTemp(value), { decimalPlaces: 0 }),
      },
      {
        title: "Grain Moisture Avg (%)",
        dataIndex: "averageMc",
        key: "averageMc",
        render: (value) => formatNumber(value, { decimalPlaces: 1 }),
      },
      {
        title: "Status",
        dataIndex: "machineOn",
        key: "machineOn",
        filters: [
          { text: "ON", value: "ON" },
          { text: "OFF", value: "OFF" },
        ],
        render(_, record) {
          return <span>{record.isMachineOn ? "ON" : "OFF"}</span>;
        },
        onCell: (record) => ({
          style: {
            backgroundColor: record.isMachineOn ? "rgb(4, 32, 253)" : "rgb(253, 48, 4)",
            color: "white",
          },
        }),
      },
      {
        title: `Target Temp (${unit})`,
        dataIndex: "plenumTemperatureSetpointF",
        key: `plenumSetpoint-${unit}`, // key changes when unit changes
        render: (value) => formatNumber(toTemp(value), { decimalPlaces: 0 }),
      },
      {
        title: `Plenum Temp (${unit})`,
        dataIndex: "actualPlenumTemperatureF",
        key: `plenumActual-${unit}`, // key changes when unit changes
        render: (value) => formatNumber(toTemp(value), { decimalPlaces: 0 }),
      },
      {
        title: `Static Pressure (InH2O)`,
        dataIndex: "staticPlenumPressure",
        key: `staticPressure-inH2O`, // key changes when unit changes
        render: (value) => formatNumber(value, { decimalPlaces: 2 }),
      },
      {
        title: "Info",
        dataIndex: "info",
        key: "info",
      },
    ],
    [unit, toTemp]
  );

  const transformedColumns = useColumnExpansion({
    columns,
    datasource: getPremierPlusTapestripDataQuery.data ?? [],
  });

  return (
    <Table
      key={unit} // force-remount table when unit flips
      loading={getPremierPlusTapestripDataQuery.isLoading}
      rowClassName={(record) =>
        record.isMachineOn ? "highlight-machine-on" : "highlight-machine-off"
      }
      scroll={{x: 'max-content'}}
      size="small"
      pagination={{ defaultPageSize: 10 }}
      dataSource={getPremierPlusTapestripDataQuery.data ?? []}
      columns={transformedColumns}
      rowKey={(record, idx) => `${record.captureTimeUtc}-${idx}`}
    />
  );
};
